import type React from "react";
import type {
  ChapterVisual,
  PlaceCoordinates,
  PlaceHistoryInstrument,
  PlaceHistorySceneVariant,
} from "@/lib/museum/types";
import { ChapterImage } from "./ChapterImage";
import styles from "./museum.module.css";

const toneClass = {
  archive: styles.worldToneArchive,
  natural: styles.worldToneNatural,
  night: styles.worldToneNight,
};

function stageLabel(
  variant: PlaceHistorySceneVariant,
  instrument: PlaceHistoryInstrument,
): string {
  const isMountain = instrument === "mountain-contour"
    || instrument === "valley-route"
    || instrument === "volcanic-ridge";
  const isCoast = instrument === "wave-coast" || instrument === "island-reef";

  if (variant === "place-land") {
    if (isMountain) return "Terrain before the resort";
    if (isCoast) return "Shoreline before surf culture";
    return "Water before organised sailing";
  }
  if (variant === "place-access") {
    if (isMountain) return "Road, rail and mountain access";
    if (isCoast) return "Travel, equipment and reach";
    if (instrument === "inland-water") return "Clubs, slipways and access";
    return "Harbours, clubs and access";
  }
  if (variant === "place-sport") {
    if (isMountain) return "The resort takes shape";
    if (isCoast) return "The break enters surf culture";
    return "The water becomes a course";
  }

  const labels: Record<
    Exclude<PlaceHistorySceneVariant, "place-land" | "place-access" | "place-sport">,
    string
  > = {
    "place-life": "Work, movement and ordinary life",
    "place-map": "The modern mapped place",
    "place-present": "The place today",
    "place-ledger": "Sources and credits",
  };
  return labels[variant];
}

function seeded(seed: number, index: number, minimum: number, range: number): number {
  const mixed = Math.imul(seed ^ Math.imul(index + 1, 2654435761), 1597334677) >>> 0;
  return minimum + (mixed % 1000) / 1000 * range;
}

function contourPath(seed: number, index: number): string {
  const y = 165 + index * 94;
  const liftA = seeded(seed, index * 4, -90, 180);
  const liftB = seeded(seed, index * 4 + 1, -80, 160);
  const liftC = seeded(seed, index * 4 + 2, -95, 190);
  return `M-100 ${y + liftA * 0.25} C250 ${y - 65 + liftA}, 430 ${y + 75 - liftB}, 760 ${y + liftB * 0.2} S1230 ${y - 80 + liftC}, 1700 ${y + liftC * 0.25}`;
}

function coordinateLabel(coordinates: PlaceCoordinates): string {
  const latitude = `${Math.abs(coordinates.latitude).toFixed(3)}°${coordinates.latitude >= 0 ? "N" : "S"}`;
  const longitude = `${Math.abs(coordinates.longitude).toFixed(3)}°${coordinates.longitude >= 0 ? "E" : "W"}`;
  return `${latitude} · ${longitude}`;
}

export function PlaceCartography({
  coordinates,
  instrument,
  seed,
}: {
  coordinates: PlaceCoordinates;
  instrument: PlaceHistoryInstrument;
  seed: number;
}): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 1600 1000"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
    >
      <rect className={styles.placeField} width="1600" height="1000" />
      <g className={styles.placeGrid}>
        <path d="M0 180 H1600 M0 500 H1600 M0 820 H1600" />
        <path d="M260 0 V1000 M800 0 V1000 M1340 0 V1000" />
      </g>
      <g className={styles.placeContours} data-place-part="contours">
        {Array.from({ length: 8 }, (_, index) => (
          <path key={index} d={contourPath(seed, index)} />
        ))}
      </g>
      <PlaceInstrumentDrawing instrument={instrument} seed={seed} />
      <g className={styles.placeCoordinates} data-place-part="coordinates">
        <circle cx="800" cy="500" r="12" />
        <circle cx="800" cy="500" r="48" />
        <path d="M800 420 V580 M720 500 H880" />
        <text x="835" y="555">{coordinateLabel(coordinates)}</text>
        {coordinates.elevation ? (
          <text x="835" y="590">{coordinates.elevation.toLocaleString("en-GB")} M</text>
        ) : null}
      </g>
    </svg>
  );
}

export function PlaceHistoryScene({
  coordinates,
  instrument,
  instrumentLabel,
  seed,
  variant,
  visual,
}: {
  coordinates: PlaceCoordinates;
  instrument: PlaceHistoryInstrument;
  instrumentLabel: string;
  seed: number;
  variant: PlaceHistorySceneVariant;
  visual?: ChapterVisual;
}): React.JSX.Element {
  return (
    <div
      className={`${styles.world} ${styles.placeHistoryWorld} ${visual ? `${styles.worldHasVisual} ${toneClass[visual.tone]}` : ""}`}
      data-place-history-world
      data-place-instrument={instrument}
      data-place-stage={variant}
      data-scene={variant}
    >
      {visual ? <ChapterImage visual={visual} eager={variant === "place-land"} /> : null}
      <div className={styles.placePlate} aria-hidden="true">
        <span>Geographic instrument</span>
        <strong>{instrumentLabel}</strong>
        <small>{stageLabel(variant, instrument)}</small>
      </div>
      <PlaceCartography coordinates={coordinates} instrument={instrument} seed={seed} />
      <div className={styles.sceneGrain} aria-hidden="true" />
      <div className={styles.sceneVignette} aria-hidden="true" />
    </div>
  );
}

function PlaceInstrumentDrawing({
  instrument,
  seed,
}: {
  instrument: PlaceHistoryInstrument;
  seed: number;
}): React.JSX.Element {
  const shift = seeded(seed, 17, -70, 140);

  if (instrument === "mountain-contour" || instrument === "valley-route" || instrument === "volcanic-ridge") {
    return (
      <g data-place-drawing={instrument}>
        <path
          className={styles.placeMass}
          d={instrument === "volcanic-ridge"
            ? `M-80 850 L360 690 L650 590 L800 ${225 + shift * 0.2} L950 590 L1240 690 L1680 850 Z`
            : `M-80 850 L240 700 L465 ${410 + shift * 0.25} L690 650 L865 ${285 - shift * 0.15} L1110 610 L1370 390 L1680 760 L1680 1000 L-80 1000 Z`}
        />
        <path
          className={styles.placeRoute}
          data-place-part="route"
          d={instrument === "valley-route"
            ? "M110 865 C370 720 545 820 770 690 C985 565 1150 670 1490 390"
            : "M170 865 C460 740 660 820 860 640 C1050 470 1190 520 1450 310"}
        />
        <g className={styles.placeAccess} data-place-part="access">
          <path d="M310 815 L565 640 M1010 560 L1240 390" />
          <circle cx="565" cy="640" r="10" />
          <circle cx="1240" cy="390" r="10" />
        </g>
      </g>
    );
  }

  if (instrument === "wave-coast" || instrument === "island-reef") {
    return (
      <g data-place-drawing={instrument}>
        <path
          className={styles.placeMass}
          d={instrument === "island-reef"
            ? `M520 650 C575 ${370 + shift * 0.25} 1025 ${360 - shift * 0.2} 1090 650 C990 780 620 785 520 650 Z`
            : `M-100 155 C270 265 360 470 520 640 C690 820 1010 760 1700 640 L1700 1000 L-100 1000 Z`}
        />
        <g className={styles.placeWater} data-place-part="water">
          <path d="M-40 330 C250 240 475 420 760 320 C1040 220 1275 390 1640 260" />
          <path d="M-40 425 C280 330 520 515 820 400 C1110 290 1320 475 1640 355" />
          <path d="M-40 520 C310 420 540 610 875 500 C1160 405 1385 560 1640 470" />
        </g>
        <path className={styles.placeRoute} data-place-part="route" d="M120 225 C430 150 600 310 870 235 C1140 160 1320 240 1510 170" />
      </g>
    );
  }

  return (
    <g data-place-drawing={instrument}>
      <path
        className={styles.placeMass}
        d={instrument === "inland-water"
          ? `M0 0 H1600 V1000 H0 Z M330 620 C390 ${300 + shift * 0.2} 1190 ${285 - shift * 0.15} 1280 620 C1170 820 430 825 330 620 Z`
          : `M-80 740 C230 660 420 760 650 610 C880 465 1015 690 1240 535 C1410 420 1535 455 1680 390 L1680 1000 L-80 1000 Z`}
      />
      <g className={styles.placeWater} data-place-part="water">
        <path d="M90 430 C350 340 535 500 800 400 C1070 300 1280 445 1510 350" />
        <path d="M110 520 C370 435 555 585 820 490 C1080 395 1290 530 1490 450" />
      </g>
      <path className={styles.placeRoute} data-place-part="route" d="M270 760 L510 475 L765 640 L1025 335 L1320 560" />
      <g className={styles.placeAccess} data-place-part="access">
        <circle cx="270" cy="760" r="15" />
        <circle cx="765" cy="640" r="15" />
        <circle cx="1320" cy="560" r="15" />
      </g>
    </g>
  );
}
